// Campaign's form state mirrors the API shape directly (camelCase field-for-field with
// CampaignRequest/CampaignResponse) -- unlike jobOfferMapper.js, there's no renaming/regrouping
// needed since Campaign has far fewer, flatter sections than Recruitment. This mapper only
// normalizes empty values before sending; the initial-state factory lives in models/campaign
// (createEmptyCampaign), mirroring models/jobOffer's createEmptyJobOffer.

import { createEmptyCompanyInfo } from "@/models/campaign";

const nullIfEmpty = (v) => (v === "" || v === undefined ? null : v);

const mapLeadSourceConfig = (lc) => {
    if (!lc) return null;
    const hasAny = lc.spreadsheetUrl || lc.sheetName || lc.excelFileUrl || lc.excelWorksheetName;
    if (!hasAny) return null;
    return {
        spreadsheetUrl: nullIfEmpty(lc.spreadsheetUrl),
        sheetName: nullIfEmpty(lc.sheetName),
        isConnected: !!lc.isConnected,
        decisionColumn: nullIfEmpty(lc.decisionColumn),
        decisionColumnAllowedValues: lc.decisionColumnAllowedValues?.length ? lc.decisionColumnAllowedValues : null,
        statusColumn: nullIfEmpty(lc.statusColumn),
        statusValueMapping: lc.statusValueMapping?.length ? lc.statusValueMapping : null,
        defaultStatus: nullIfEmpty(lc.defaultStatus),
        // Task-25/28 -- Excel Online (Microsoft Graph), kept alongside the Google-specific fields above
        // rather than renaming them (already used by saved campaigns).
        excelFileUrl: nullIfEmpty(lc.excelFileUrl),
        excelWorksheetName: nullIfEmpty(lc.excelWorksheetName),
        excelConnectionId: lc.excelConnectionId || null,
    };
};

const mapEscalationConfig = (ec) => {
    if (!ec) return null;
    const hasAny = ec.conditions?.length || ec.transactionValueThreshold != null || ec.handoffSummaryTemplate;
    if (!hasAny) return null;
    return {
        conditions: ec.conditions ?? [],
        transactionValueThreshold: ec.transactionValueThreshold ?? null,
        handoffSummaryTemplate: nullIfEmpty(ec.handoffSummaryTemplate),
    };
};

const mapCompanyInfo = (ci) => {
    if (!ci) return null;
    const {
        companyName, brand, website, shortDescription, whatWeDo, whyChooseUs,
        keyAdvantages, experience, references, certificates, awards, partners,
        serviceArea, locations, operatingHours,
    } = ci;
    const hasAny = [companyName, brand, website, shortDescription, whatWeDo, whyChooseUs, experience, serviceArea, operatingHours]
        .some(v => v) || [keyAdvantages, references, certificates, awards, partners, locations].some(a => a?.length);
    if (!hasAny) return null;
    return {
        companyName: nullIfEmpty(companyName), brand: nullIfEmpty(brand), website: nullIfEmpty(website),
        shortDescription: nullIfEmpty(shortDescription), whatWeDo: nullIfEmpty(whatWeDo), whyChooseUs: nullIfEmpty(whyChooseUs),
        keyAdvantages: keyAdvantages ?? [], experience: nullIfEmpty(experience), references: references ?? [],
        certificates: certificates ?? [], awards: awards ?? [], partners: partners ?? [],
        serviceArea: nullIfEmpty(serviceArea), locations: locations ?? [], operatingHours: nullIfEmpty(operatingHours),
    };
};

const mapStatusAutomationRule = (r) => ({
    id: r.id, status: r.status, isActive: !!r.isActive,
    agentAction: nullIfEmpty(r.agentAction),
    primaryChannel: r.primaryChannel || null, fallbackChannel: r.fallbackChannel || null,
    channelSequenceMode: r.channelSequenceMode || null,
    delayMinutes: r.delayMinutes === "" || r.delayMinutes == null ? null : Number(r.delayMinutes),
    parallelCommunication: !!r.parallelCommunication, startMessageTemplate: nullIfEmpty(r.startMessageTemplate),
});

const mapCommunicationChannels = (cc) => {
    if (!cc) return null;
    const wa = cc.whatsApp ?? {};
    const sms = cc.sms ?? {};
    const email = cc.email ?? {};
    const hasAny = cc.channelOrder?.length || cc.delayBetweenChannelsMinutes != null || cc.parallelCommunication
        || wa.enabled || sms.enabled || email.enabled;
    if (!hasAny) return null;
    return {
        channelOrder: cc.channelOrder ?? [],
        delayBetweenChannelsMinutes: cc.delayBetweenChannelsMinutes ?? null,
        parallelCommunication: !!cc.parallelCommunication,
        whatsApp: {
            enabled: !!wa.enabled, senderNumber: nullIfEmpty(wa.senderNumber), fallbackChannel: wa.fallbackChannel || null,
            startMessageTemplate: nullIfEmpty(wa.startMessageTemplate), retryRules: nullIfEmpty(wa.retryRules),
        },
        sms: {
            enabled: !!sms.enabled, senderIdOrNumber: nullIfEmpty(sms.senderIdOrNumber), fallbackChannel: sms.fallbackChannel || null,
            messageTemplate: nullIfEmpty(sms.messageTemplate),
        },
        email: {
            enabled: !!email.enabled, senderAddress: nullIfEmpty(email.senderAddress), senderName: nullIfEmpty(email.senderName),
            subject: nullIfEmpty(email.subject), bodyTemplate: nullIfEmpty(email.bodyTemplate), footer: nullIfEmpty(email.footer),
            webAgentLink: nullIfEmpty(email.webAgentLink), allowAttachments: !!email.allowAttachments,
        },
    };
};

const mapFollowUpAttempt = (a) => ({
    id: a.id, delayMinutes: a.delayMinutes === "" || a.delayMinutes == null ? 0 : Number(a.delayMinutes),
    channel: a.channel || null, messageTemplate: nullIfEmpty(a.messageTemplate),
});

const mapFollowUpConfig = (fc) => {
    if (!fc) return null;
    const attempts = (fc.attempts ?? []).map(mapFollowUpAttempt);
    const hasAny = attempts.length > 0 || fc.stopConditions?.length;
    if (!hasAny) return null;
    return { attempts, stopConditions: fc.stopConditions ?? [] };
};

const mapOutputIntegration = (o) => ({
    id: o.id, targetSystem: o.targetSystem, trigger: o.trigger, action: o.action,
    isActive: !!o.isActive,
    // Task-23: the "Aktualizuj arkusz źródłowy leadów" switch is hidden in the UI for now -- force false
    // here too so campaigns saved before this change (which may have `true`) get corrected on next save.
    updateSourceSheet: false,
    isConnected: !!o.isConnected,
    spreadsheetUrl: nullIfEmpty(o.spreadsheetUrl), sheetName: nullIfEmpty(o.sheetName),
    // Task-25/29 -- Excel Online (Microsoft Graph), same "add alongside" reasoning as mapLeadSourceConfig.
    excelFileUrl: nullIfEmpty(o.excelFileUrl), excelWorksheetName: nullIfEmpty(o.excelWorksheetName),
    excelConnectionId: o.excelConnectionId || null,
    keyTargetColumn: nullIfEmpty(o.keyTargetColumn),
    fieldMapping: (o.fieldMapping ?? []).filter(m => m.sourceField && m.targetField),
});

const mapConversationStyle = (cs) => {
    if (!cs) return null;
    const forbiddenAssumptions = (cs.forbiddenAssumptions ?? []).filter(Boolean);
    const hasAny = cs.agentName || cs.introductionTemplate || cs.brandStyle || cs.addressForm
        || cs.useEmoji || cs.messageLength || cs.maxQuestionsPerMessage != null || cs.questionStyle
        || cs.inConversationFollowUpRules || cs.closingStyle || cs.salesInitiativeLevel
        || cs.escalationRules || forbiddenAssumptions.length;
    if (!hasAny) return null;
    return {
        agentName: nullIfEmpty(cs.agentName), introductionTemplate: nullIfEmpty(cs.introductionTemplate),
        brandStyle: nullIfEmpty(cs.brandStyle), addressForm: cs.addressForm || null,
        useEmoji: !!cs.useEmoji, messageLength: cs.messageLength || null,
        maxQuestionsPerMessage: cs.maxQuestionsPerMessage === "" || cs.maxQuestionsPerMessage == null ? null : Number(cs.maxQuestionsPerMessage),
        questionStyle: nullIfEmpty(cs.questionStyle), inConversationFollowUpRules: nullIfEmpty(cs.inConversationFollowUpRules),
        closingStyle: nullIfEmpty(cs.closingStyle), salesInitiativeLevel: cs.salesInitiativeLevel || null,
        escalationRules: nullIfEmpty(cs.escalationRules), forbiddenAssumptions,
    };
};

const mapProductPrice = (price) => (price ? { ...price, billingCycle: price.billingCycle || null } : null);

const mapProduct = (p) => ({
    id: p.id, name: p.name, productType: p.productType || null,
    category: nullIfEmpty(p.category), shortDescription: nullIfEmpty(p.shortDescription),
    fullDescription: nullIfEmpty(p.fullDescription), manufacturer: nullIfEmpty(p.manufacturer), brand: nullIfEmpty(p.brand),
    model: nullIfEmpty(p.model), variant: nullIfEmpty(p.variant), applications: p.applications ?? [],
    benefits: p.benefits ?? [], usp: p.usp ?? [], parameters: p.parameters ?? {},
    price: mapProductPrice(p.price), availability: p.availability ?? null, usageInstructions: nullIfEmpty(p.usageInstructions),
    safetyInfo: p.safetyInfo ?? null, attachments: p.attachments ?? [], faq: p.faq ?? [],
});

export const toApiRequest = (scenarioId, campaign) => ({
    scenarioId,
    ownerId: campaign.ownerId ?? null,
    name: campaign.name,
    agentDisplayName: nullIfEmpty(campaign.agentDisplayName),
    description: nullIfEmpty(campaign.description),
    languages: campaign.languages ?? [],
    companyInfo: mapCompanyInfo(campaign.companyInfo),
    products: (campaign.products ?? []).map(mapProduct),
    salesArguments: campaign.salesArguments ?? [],
    objections: campaign.objections ?? [],
    conversionGoals: campaign.conversionGoals ?? [],
    sendNotifications: campaign.sendNotifications ?? false,
    notificationEmails: campaign.notificationEmails?.length ? campaign.notificationEmails.join(",") : null,
    leadSourceType: campaign.leadSourceType || null,
    leadSourceConfig: mapLeadSourceConfig(campaign.leadSourceConfig),
    leadFieldMappings: (campaign.leadFieldMappings ?? []).filter(m => m.sourceColumn),
    customFieldMappings: (campaign.customFieldMappings ?? []).filter(m => m.fieldName && m.sourceColumn),
    scoringOverrides: campaign.scoringOverrides ?? [],
    localCriteria: campaign.localCriteria ?? [],
    qualifyingQuestions: campaign.qualifyingQuestions ?? [],
    qualificationCriteria: campaign.qualificationCriteria ?? [],
    disqualificationAction: campaign.disqualificationAction || null,
    disqualificationActionDetail: nullIfEmpty(campaign.disqualificationActionDetail),
    escalationConfig: mapEscalationConfig(campaign.escalationConfig),
    statusAutomationRules: (campaign.statusAutomationRules ?? []).filter(r => r.status).map(mapStatusAutomationRule),
    communicationChannels: mapCommunicationChannels(campaign.communicationChannels),
    followUpConfig: mapFollowUpConfig(campaign.followUpConfig),
    outputIntegrations: (campaign.outputIntegrations ?? []).map(mapOutputIntegration),
    conversationStyle: mapConversationStyle(campaign.conversationStyle),
    status: campaign.status || "Draft",
});

export const toUpdateRequest = (campaign) => toApiRequest(campaign.scenarioId, campaign);

export const fromApiResponse = (r) => ({
    id: r.id,
    name: r.name ?? "",
    agentDisplayName: r.agentDisplayName ?? "",
    description: r.description ?? "",
    languages: r.languages ?? [],
    companyInfo: r.companyInfo ?? createEmptyCompanyInfo(),
    products: (r.products ?? []).map((p) => ({ ...p, productType: p.productType || null, price: mapProductPrice(p.price) })),
    salesArguments: r.salesArguments ?? [],
    objections: r.objections ?? [],
    conversionGoals: r.conversionGoals ?? [],
    sendNotifications: r.sendNotifications ?? false,
    notificationEmails: r.notificationEmails ? r.notificationEmails.split(",").map(s => s.trim()).filter(Boolean) : [],
    leadSourceType: r.leadSourceType ?? null,
    leadSourceConfig: {
        spreadsheetUrl: r.leadSourceConfig?.spreadsheetUrl ?? "",
        sheetName: r.leadSourceConfig?.sheetName ?? "",
        isConnected: r.leadSourceConfig?.isConnected ?? false,
        decisionColumn: r.leadSourceConfig?.decisionColumn ?? "",
        decisionColumnAllowedValues: r.leadSourceConfig?.decisionColumnAllowedValues ?? [],
        statusColumn: r.leadSourceConfig?.statusColumn ?? "",
        statusValueMapping: r.leadSourceConfig?.statusValueMapping ?? [],
        defaultStatus: r.leadSourceConfig?.defaultStatus ?? "",
        excelFileUrl: r.leadSourceConfig?.excelFileUrl ?? "",
        excelWorksheetName: r.leadSourceConfig?.excelWorksheetName ?? "",
        excelConnectionId: r.leadSourceConfig?.excelConnectionId ?? null,
    },
    leadFieldMappings: r.leadFieldMappings ?? [],
    customFieldMappings: r.customFieldMappings ?? [],
    scoringOverrides: r.scoringOverrides ?? [],
    localCriteria: r.localCriteria ?? [],
    qualifyingQuestions: r.qualifyingQuestions ?? [],
    qualificationCriteria: r.qualificationCriteria ?? [],
    disqualificationAction: r.disqualificationAction ?? null,
    disqualificationActionDetail: r.disqualificationActionDetail ?? "",
    escalationConfig: {
        conditions: r.escalationConfig?.conditions ?? [],
        transactionValueThreshold: r.escalationConfig?.transactionValueThreshold ?? null,
        handoffSummaryTemplate: r.escalationConfig?.handoffSummaryTemplate ?? "",
    },
    statusAutomationRules: r.statusAutomationRules ?? [],
    communicationChannels: {
        channelOrder: r.communicationChannels?.channelOrder?.length ? r.communicationChannels.channelOrder : ["WhatsApp", "Sms", "Email"],
        delayBetweenChannelsMinutes: r.communicationChannels?.delayBetweenChannelsMinutes ?? null,
        parallelCommunication: r.communicationChannels?.parallelCommunication ?? false,
        whatsApp: {
            enabled: r.communicationChannels?.whatsApp?.enabled ?? false,
            senderNumber: r.communicationChannels?.whatsApp?.senderNumber ?? "",
            fallbackChannel: r.communicationChannels?.whatsApp?.fallbackChannel ?? null,
            startMessageTemplate: r.communicationChannels?.whatsApp?.startMessageTemplate ?? "",
            retryRules: r.communicationChannels?.whatsApp?.retryRules ?? "",
        },
        sms: {
            enabled: r.communicationChannels?.sms?.enabled ?? false,
            senderIdOrNumber: r.communicationChannels?.sms?.senderIdOrNumber ?? "",
            fallbackChannel: r.communicationChannels?.sms?.fallbackChannel ?? null,
            messageTemplate: r.communicationChannels?.sms?.messageTemplate ?? "",
        },
        email: {
            enabled: r.communicationChannels?.email?.enabled ?? false,
            senderAddress: r.communicationChannels?.email?.senderAddress ?? "",
            senderName: r.communicationChannels?.email?.senderName ?? "",
            subject: r.communicationChannels?.email?.subject ?? "",
            bodyTemplate: r.communicationChannels?.email?.bodyTemplate ?? "",
            footer: r.communicationChannels?.email?.footer ?? "",
            webAgentLink: r.communicationChannels?.email?.webAgentLink ?? "",
            allowAttachments: r.communicationChannels?.email?.allowAttachments ?? false,
        },
    },
    followUpConfig: {
        attempts: r.followUpConfig?.attempts ?? [],
        stopConditions: r.followUpConfig?.stopConditions ?? ["LeadResponded", "LeadConverted", "MeetingBooked", "StatusChangedManually", "LeadRequestedNoContact"],
    },
    outputIntegrations: r.outputIntegrations ?? [],
    conversationStyle: {
        agentName: r.conversationStyle?.agentName ?? "",
        introductionTemplate: r.conversationStyle?.introductionTemplate ?? "",
        brandStyle: r.conversationStyle?.brandStyle ?? "",
        addressForm: r.conversationStyle?.addressForm ?? null,
        useEmoji: r.conversationStyle?.useEmoji ?? false,
        messageLength: r.conversationStyle?.messageLength ?? null,
        maxQuestionsPerMessage: r.conversationStyle?.maxQuestionsPerMessage ?? null,
        questionStyle: r.conversationStyle?.questionStyle ?? "",
        inConversationFollowUpRules: r.conversationStyle?.inConversationFollowUpRules ?? "",
        closingStyle: r.conversationStyle?.closingStyle ?? "",
        salesInitiativeLevel: r.conversationStyle?.salesInitiativeLevel ?? null,
        escalationRules: r.conversationStyle?.escalationRules ?? "",
        forbiddenAssumptions: r.conversationStyle?.forbiddenAssumptions?.length
            ? r.conversationStyle.forbiddenAssumptions
            : ["Nie wymyślaj cen", "Nie obiecuj terminów", "Nie wymyślaj rabatów"],
    },
    status: r.status ?? "Draft",
    ownerId: r.ownerId ?? null,
    scenarioId: r.scenario?.id ?? null,
});
